import { FormattedMessage, useIntl } from 'react-intl'

import Box from 'app/components/base/Box'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import useCurrencyState from 'app/hooks/useCurrencyState'

import ShippingReturnRulesItem from '../../components/ShippingReturnRulesItem'
import { useShippingReturnRules } from '../hooks/useShippingReturnRules'

import ClickAndCollectBlock from './components/ClickAndCollectBlock'
import ImportDutiesHint from './components/ImportDutiesHint'
import ReturnsInfoBlock from './components/ReturnsInfoBlock'
import ShippingBlock from './components/ShippingBlock'
import ShippingScopeHeader from './components/ShippingScopeHeader'
import msg from './ShippingReturnRules.messages'

interface ShippingReturnRulesProps {
  isPreview?: boolean
  shippingType: string[]
  packaging?: string
  weight?: string
  slug: string
  shippingPolicyInfo?: string
  isAuthUserListing: boolean
  shipping?: ShippingModel
  collectionTimeFrames?: TimeFrameModel[]
  isPersonalSeller?: boolean
  ownShippingCurrency?: string
}

const ShippingReturnRules: RFC<ShippingReturnRulesProps> = ({
  isPreview,
  shippingType,
  packaging,
  weight,
  shippingPolicyInfo,
  slug,
  isAuthUserListing,
  shipping,
  collectionTimeFrames,
  isPersonalSeller,
  ownShippingCurrency
}) => {
  const { formatMessage } = useIntl()
  const { currency } = useCurrencyState()

  const {
    country,
    shippingCost,
    collectionLocation,
    showAddressActions,
    isNoAddress,
    isSeveralAddresses,
    addressOptions,
    calculateShippingCost,
    shippingMethods,
    ownShippingCurrency: resolvedOwnShippingCurrency,
    returnsInfo,
    shippingScope,
    hasShipShipping,
    hasClickAndCollect,
    isInternationalEnabled
  } = useShippingReturnRules({
    shipping,
    shippingType,
    isPreview,
    isAuthUserListing,
    slug,
    isPersonalSeller,
    ownShippingCurrency
  })

  return (
    <Box sx={{ pb: 3 }}>
      <Stack spacing={2}>
        <ShippingScopeHeader shippingScope={shippingScope} country={country} />

        {hasClickAndCollect && (
          <ClickAndCollectBlock
            location={collectionLocation}
            collectionTimeFrames={collectionTimeFrames}
          />
        )}

        {hasShipShipping && (
          <ShippingBlock
            country={country}
            currency={currency}
            ownShippingCurrency={resolvedOwnShippingCurrency}
            cost={shippingCost}
            shippingMethods={shippingMethods}
            showAddressActions={showAddressActions}
            isNoAddress={isNoAddress}
            isSeveralAddresses={isSeveralAddresses}
            addressOptions={addressOptions}
            calculateShippingCost={calculateShippingCost}
          />
        )}

        {isInternationalEnabled && hasShipShipping && <ImportDutiesHint />}

        {packaging && (
          <>
            <Text variant="subtitle1">
              <FormattedMessage
                defaultMessage="Packaging: {packaging}"
                values={{ packaging }}
              />
            </Text>

            <Text variant="body2" color="text.third">
              {weight}
            </Text>
          </>
        )}
      </Stack>

      {shippingPolicyInfo && (
        <Stack spacing={2} sx={{ marginTop: 3 }}>
          <Text variant="body2" sx={{ fontWeight: 500 }}>
            {formatMessage(msg.shippingInfo)}
          </Text>

          <ShippingReturnRulesItem title="" value={shippingPolicyInfo} />
        </Stack>
      )}

      <ReturnsInfoBlock returnsInfo={returnsInfo} />
    </Box>
  )
}

export default ShippingReturnRules
